import {
  PageHeader, Table, FilterChip, Skeleton, SkeletonButton, SkeletonRegion,
} from '@parsina/framework';
import { C, RADIUS } from '@parsina/framework/tokens';
import { STACK } from '../../constants/density.js';
import { useTranslation } from '../../i18n';
import useResponsive from '../../hooks/useResponsive';
import {
  MobileScreen, MobileHeader, MobileFilterBar, MobileStatTiles, MobileCardSkeleton,
} from '../../mobile';

/**
 * «بیماران» while it loads — the page's own shape, desktop and phone.
 *
 * This file is the router's Suspense fallback for the lazy `Patients` chunk AND
 * the screen's own first-load state, so the transition from "navigating" to
 * "fetching" to "loaded" draws the same header, KPI row, toolbar and table the
 * whole way and nothing jumps (DESIGN.md §"Loading & skeleton"). It therefore
 * imports only the kit, the tokens, the density scale, i18n and the mobile
 * primitives — never the screen, its hooks or its data.
 *
 * Static chrome renders for REAL: the page title, the segment chips (without
 * their clinic-wide counts, which are data), the column headers. Anything that
 * needs a query is a block.
 */

// ── Shared helpers ─────────────────────────────────────────────────────────────

/**
 * A real kit `Table` whose body is placeholder rows.
 *
 * The kit's `Table` renders its own `<tbody>` from `rows` and has no slot for
 * foreign `<tr>`s, so a `SkeletonRow` cannot be placed inside it. Mapping every
 * column's `render` to a block and feeding it `rows` empty objects keeps the
 * REAL header (labels, widths, sort affordances, sticky strip) and the real
 * 52/44px rows, so the geometry the data lands in is exactly the geometry the
 * placeholder held.
 */
export function TableSkeletonBody({
  columns, rows = 7, dense = false, label,
  /** False when a parent composite already announces the loading state. */
  standalone = true,
  style, ...rest
}) {
  const cols = columns.map((col, i) => ({
    ...col,
    // The select-all checkbox is a control the pending body cannot serve.
    header: typeof col.header === 'string' ? col.header : null,
    render: () => (
      <Skeleton
        width={col.skeletonWidth || (i === 0 ? '30%' : '70%')}
        height={i === 0 ? 16 : 13}
        style={col.align === 'center' ? { marginInline: 'auto' } : undefined}
      />
    ),
  }));
  const placeholders = Array.from({ length: rows }, (_, i) => ({ id: `skeleton-${i}` }));
  return (
    <div
      {...(standalone ? { role: 'status', 'aria-busy': 'true', 'aria-label': label } : { 'aria-hidden': true })}
      data-state="loading"
      style={style}
      {...rest}
    >
      <Table columns={cols} rows={placeholders} rowKey={(r) => r.id} dense={dense} />
    </div>
  );
}

/** One `KpiCard`-shaped block: the kit card's padding, radius, icon box and type. */
function KpiCardSkeleton() {
  return (
    <div aria-hidden style={{
      background: '#fff', borderRadius: 12, padding: '16px 20px',
      display: 'flex', alignItems: 'center', gap: 14,
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)', flex: 1, minWidth: 140,
    }}>
      <Skeleton width={44} height={44} radius={12} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
        <Skeleton width="45%" height={22} />
        <Skeleton width="65%" height={12} />
      </div>
    </div>
  );
}

/** The placeholder for one `MobilePatientCard`: a 34px avatar, name + phone line, one chip. */
export function PatientCardSkeleton(props) {
  return (
    <MobileCardSkeleton
      avatar={34}
      metaLines={1}
      badge={0}
      chips={1}
      style={{ paddingInlineEnd: '3.5rem' }}
      {...props}
    />
  );
}

/** The placeholder for one `MobileBroadcastCard`: channel line + badge, a two-line body, a meta line. */
export function BroadcastCardSkeleton(props) {
  return <MobileCardSkeleton avatar={0} metaLines={3} badge={64} {...props} />;
}

const noop = () => {};

const SEGMENT_KEYS = [
  'patients.segments.all', 'patients.segments.pending', 'patients.segments.debtor',
  'patients.segments.followup', 'patients.segments.inactive',
];

const SURFACE = {
  background: '#fff', border: `1px solid ${C.border}`, borderRadius: 14,
  overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.04)',
};

// ── Desktop ────────────────────────────────────────────────────────────────────

function ListSkeletonWeb({ t, label }) {
  const columns = [
    { key: 'pick', header: null, align: 'center', width: 96, skeletonWidth: 18 },
    { key: 'patient', header: t('patients.columns.patient') },
    { key: 'contact', header: t('patients.columns.contact'), width: 210 },
    { key: 'insurance', header: t('patients.fields.insurance'), width: 132 },
    { key: 'visits', header: t('patients.columns.visits'), width: 172 },
    { key: 'finance', header: t('patients.columns.finance'), width: 120 },
    { key: 'actions', header: t('common.fields.actions'), width: 150, skeletonWidth: 40 },
  ];

  return (
    <SkeletonRegion
      label={label}
      data-testid="patients-skeleton"
      style={{
      display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0,
      position: 'relative', background: C.bg,
      }}>
      <PageHeader title={t('nav.items.patients')} sub={t('patients.page.sub')}>
        <SkeletonButton width={150} />
        <SkeletonButton width={150} />
      </PageHeader>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 32px 24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: STACK.regular }}>
          <div style={{ display: 'flex', gap: STACK.regular, flexWrap: 'wrap' }}>
            {Array.from({ length: 4 }, (_, i) => <KpiCardSkeleton key={i} />)}
          </div>

          <div style={{
            paddingBlock: STACK.tight, display: 'flex', flexDirection: 'column', gap: STACK.tight,
          }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {SEGMENT_KEYS.map((key, i) => (
                <FilterChip key={key} label={t(key)} active={i === 0} onClick={noop} />
              ))}
            </div>
            <div aria-hidden style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <Skeleton height={44} radius={RADIUS.control} style={{ flex: 1, minWidth: 280 }} />
              <Skeleton width={140} height={44} radius={RADIUS.control} />
              <Skeleton width={140} height={44} radius={RADIUS.control} />
              <Skeleton width={150} height={44} radius={RADIUS.control} />
              <Skeleton width={180} height={44} radius={RADIUS.control} />
            </div>
          </div>

          <div style={SURFACE}>
            <TableSkeletonBody columns={columns} rows={7} standalone={false} />
          </div>
        </div>
      </div>
    </SkeletonRegion>
  );
}

function BroadcastsSkeletonWeb({ t, label }) {
  const columns = [
    { key: 'when', header: t('patients.broadcasts.columns.date'), width: 150 },
    { key: 'channel', header: t('patients.broadcasts.columns.channel'), width: 120 },
    { key: 'body', header: t('patients.broadcasts.columns.body') },
    { key: 'audience', header: t('patients.broadcasts.columns.recipients'), width: 160 },
    { key: 'result', header: t('patients.broadcasts.columns.result'), width: 150 },
    { key: 'sender', header: t('patients.broadcasts.columns.sender'), width: 130 },
  ];

  return (
    <SkeletonRegion
      label={label}
      data-testid="patients-skeleton"
      data-view="broadcasts"
      style={{ display: 'flex', flexDirection: 'column', gap: STACK.regular, padding: '16px 32px 24px', background: C.bg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <SkeletonButton size="icon" />
        <span style={{ fontSize: 13, color: C.textMuted }}>{t('nav.items.patients')}</span>
        <span style={{ color: C.textMuted }}>/</span>
        <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>{t('patients.broadcasts.title')}</h2>
      </div>

      <div aria-hidden style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Skeleton width={240} height={44} radius={RADIUS.control} />
        <Skeleton width={130} height={44} radius={RADIUS.control} />
        <Skeleton width={140} height={44} radius={RADIUS.control} />
        <Skeleton height={44} radius={RADIUS.control} style={{ flex: 1, minWidth: 220 }} />
      </div>

      <div style={SURFACE}>
        <TableSkeletonBody columns={columns} rows={5} standalone={false} />
      </div>
    </SkeletonRegion>
  );
}

export function PatientsSkeletonWeb({ view = 'list' }) {
  const { t } = useTranslation();
  const label = t('common.state.loading');
  return view === 'broadcasts'
    ? <BroadcastsSkeletonWeb t={t} label={label} />
    : <ListSkeletonWeb t={t} label={label} />;
}

// ── Mobile ─────────────────────────────────────────────────────────────────────

function ListSkeletonMobile({ t, label }) {
  const chips = SEGMENT_KEYS.map((key, i) => ({ id: key, label: t(key), active: i === 0 }));
  const tiles = [
    { label: t('patients.kpi.total') },
    { label: t('patients.kpi.pending') },
    { label: t('patients.kpi.debtors') },
    { label: t('patients.kpi.followUpsToday') },
  ];

  return (
    <MobileScreen
      header={(
        <MobileHeader
          sticky
          title={t('nav.items.patients')}
          subtitle={t('patients.page.sub')}
        />
      )}
      data-state="loading"
      data-testid="patients-skeleton"
    >
      <SkeletonRegion label={label}>
        <MobileFilterBar chips={chips} onSelect={() => {}} />

        <MobileStatTiles tiles={tiles} columns={tiles.length} loading />

        <div style={{ padding: '0.75rem 1rem 0', display: 'grid', gap: `${STACK.tight}px` }}>
          {Array.from({ length: 6 }, (_, i) => <PatientCardSkeleton key={i} />)}
        </div>
      </SkeletonRegion>
    </MobileScreen>
  );
}

const BROADCAST_STATE_KEYS = [
  'patients.broadcasts.filters.stateAll', 'patients.broadcasts.state.runningShort',
  'patients.broadcasts.state.done', 'patients.broadcasts.state.halted',
  'patients.broadcasts.state.cancelled',
];

function BroadcastsSkeletonMobile({ t, label }) {
  const chips = BROADCAST_STATE_KEYS.map((key, i) => ({ id: key, label: t(key), active: i === 0 }));
  return (
    <MobileScreen
      header={<MobileHeader sticky title={t('patients.broadcasts.title')} />}
      data-state="loading"
      data-testid="patients-skeleton"
      data-view="broadcasts"
    >
      <SkeletonRegion label={label}>
        <MobileFilterBar chips={chips} onSelect={() => {}} />
        <div style={{ padding: '0.75rem 1rem 0', display: 'grid', gap: `${STACK.tight}px` }}>
          {Array.from({ length: 5 }, (_, i) => <BroadcastCardSkeleton key={i} />)}
        </div>
      </SkeletonRegion>
    </MobileScreen>
  );
}

export function PatientsSkeletonMobile({ view = 'list' }) {
  const { t } = useTranslation();
  const label = t('common.state.loading');
  return view === 'broadcasts'
    ? <BroadcastsSkeletonMobile t={t} label={label} />
    : <ListSkeletonMobile t={t} label={label} />;
}

export default function PatientsSkeleton(props) {
  const { isMobile } = useResponsive();
  return isMobile ? <PatientsSkeletonMobile {...props} /> : <PatientsSkeletonWeb {...props} />;
}
